import { useCallback, useEffect, useRef, useState } from "react";
import { ModalShell } from "../ModalShell";
import {
  useChatGptLink,
  type ChatGptAccountsInfo,
  type ChatGptPrepareState,
  type ChatGptProviderInfo,
  type ChatGptStatus,
  type ChatGptUsage,
} from "../../hooks/useChatGptLink";
import {
  CHATGPT_DEVICE_CODE_SETTING,
  CHATGPT_SETTINGS_URL,
  SUPPORT_EMAIL,
} from "../../config/links";
import {
  APPLYING_POLL_MS,
  APPLYING_TIMEOUT_MS,
  CODE_TTL_SECONDS,
  RETRY_COOLDOWN_SECONDS,
  formatCountdown,
  hostOf,
} from "./chatgptSwitch";
import dialogStyles from "../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./ChatGptSwitchModal.module.css";
import { Skeleton } from "./Skeleton";
import type { AiMode } from "../../types/openclaw";

type Props = {
  tenantId: string;
  instanceName: string;
  // Whether the agent is already on the ChatGPT subscription — decides whether
  // this opens as the switch flow or the "you're connected" panel.
  // Which account it's linked to is fetched on mount rather than passed in, so
  // a customer's OpenAI address never rides along in the instance list payload.
  linked: boolean;
  // The tenant's selected AI mode. A ChatGPT-mode instance runs an `openai/`
  // model from provisioning, before any login, so for it only the link that
  // activation records after verifying the cutover means "connected".
  aiMode?: AiMode;
  onClose: () => void;
  // Fired after a successful activate or account change so the dashboard re-reads the
  // instance (the AI-credits KPI and the switch prompt both key off it).
  //
  // Carries whose account pays once the change has landed, because every call
  // site below has just been told it — by the box, or by the endpoint that did
  // the reordering. Handing it over is what saves the dashboard a second ~36s
  // read of the customer's instance for something we already hold. Null means
  // this path genuinely can't say (the box came back on something other than
  // ChatGPT), and the caller is free to go and ask.
  onChanged: (billedAccount: string | null) => void;
};

// Order matters and is not arbitrary. OpenAI rejects the code at the moment it
// is ENTERED unless "Enable device code authorization for Codex" is already on
// ("Enable device code authorization for Codex in ChatGPT Security Settings,
// then run codex login --device-auth again"). Showing the code first therefore
// walks every customer into a dead end and burns a 15-minute code while they
// go hunting for a toggle. Setting first, then code.
type Step = "intro" | "setting" | "code" | "done";

// The instance renders a device code in ~40s. Past this we stop implying it's
// still coming and point at the retry, rather than spinning forever on a login
// that died at launch.
const CODE_WAIT_WARN_SECONDS = 90;

// The status read is file reads on the instance (~50ms) in every state — it runs
// no `openclaw` command at all, which is what makes polling it safe. Poll tight
// before a code exists so it reaches the customer the moment it renders; back off
// afterwards, because the only thing left to notice is them finishing at OpenAI
// and that is not urgent to the second.
const CODE_POLL_MS = 3000;
const AUTH_POLL_MS = 10000;

export const ChatGptSwitchModal = ({
  tenantId,
  instanceName,
  linked,
  aiMode,
  onClose,
  onChanged,
}: Props): JSX.Element => {
  const {
    busy, loadPanel, prepare, pollPrepare, startLink, refreshStatus,
    loadProvider, loadUsage, activate, setDefaultAccount, addFallbackAccount,
  } = useChatGptLink(tenantId);
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  // What the instance says it's running on. Null until the box answers; until
  // then we trust the `linked` prop, which comes from our database and can be
  // wrong for any instance set up outside this flow.
  const [provider, setProvider] = useState<ChatGptProviderInfo | null>(null);
  // Plan allowance, from the slowest read. Null means "still checking", never
  // "broken" — nothing depends on it.
  const [usage, setUsage] = useState<ChatGptUsage | null>(null);
  // Set when the customer chooses to connect a DIFFERENT ChatGPT account, so
  // the switch flow renders over the connected panel.
  const [relinking, setRelinking] = useState<boolean>(false);
  // What a new connection is FOR. Adding a spare and changing who pays are
  // separate intents on purpose: folding them together left the customer no say
  // over which account a newly-connected one displaced.
  const [relinkIntent, setRelinkIntent] = useState<"default" | "fallback">("default");

  const [step, setStep] = useState<Step>("intro");
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState<boolean>(false);
  // Null until the first preflight answers. "Installing" means the Codex add-on
  // is being installed on the box in the background — the customer waits it out
  // on the setting step rather than on a spinner.
  const [prepareState, setPrepareState] = useState<ChatGptPrepareState | null>(null);
  const [code, setCode] = useState<{ url: string; userCode: string } | null>(null);
  const [remaining, setRemaining] = useState<number>(CODE_TTL_SECONDS);
  // Seconds spent on the code step with no code yet, so a login that never
  // renders one can offer a retry instead of spinning forever.
  const [waited, setWaited] = useState<number>(0);
  const [status, setStatus] = useState<ChatGptStatus | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  // A cutover that reported an edge failure but is probably landing anyway.
  // Applying the change RESTARTS the instance, so the window where it cannot
  // answer is EXPECTED - rendering that as a red error sent the customer into a
  // retry loop on 2026-08-19, and every retry cost the box more work.
  const [applying, setApplying] = useState<boolean>(false);
  // Seconds until the confirm button is live again.
  const [cooldown, setCooldown] = useState<number>(0);
  // Set when "Add a backup account" completes, so the done step can say what
  // actually happened. A spare changes no models and does not move billing, and
  // the switch success copy ("is now running on <email>") is false on this path.
  const [backupAdded, setBackupAdded] = useState<
    { backup: string; billed: string | null } | null
  >(null);

  const titleId = "chatgpt-switch-dialog-title";
  // Selected but not yet activated. The box already reports ChatGPT, so neither
  // its model nor a freshly written OAuth profile proves the cutover landed.
  const awaitingActivation = aiMode === "chatgpt" && !linked;
  // Held in a ref, and deliberately kept OUT of the panel effect's deps below.
  // InstanceHero passes a fresh arrow on every render and re-renders on every
  // claws SSE snapshot (a heartbeat every 30s at minimum), so depending on it
  // directly re-ran the whole panel read — a ~27s /provider plus a ~26s /usage
  // round-trip at the customer's instance — for as long as the modal stayed open.
  const onChangedRef = useRef(onChanged);
  useEffect(() => {
    onChangedRef.current = onChanged;
  });
  // Both polls can outlast their own interval (a status read is ~18s once there
  // is a code to check, a prepare read ~13s), so without an in-flight guard they
  // would stack up round-trips on the instance.
  const statusInFlight = useRef<boolean>(false);
  const prepareInFlight = useRef<boolean>(false);
  // Same guard for the settle poll below, and it matters most there: it runs at
  // 5s against a ~27s /provider read, on a box that is mid-restart.
  const providerInFlight = useRef<boolean>(false);
  // The account the cutover in flight is FOR. The settle poll needs it: on a
  // re-link the box is already on an `openai/` primary, so "are we on ChatGPT"
  // is true before anything has landed and would report success for a cutover
  // that never happened. Whose account is billed is the signal that moved.
  const pendingAccount = useRef<string | null>(null);

  // Establish what this instance is really running on. Runs on EVERY open, not
  // just when our database says linked: that flag is written only when someone
  // switches through this dialog, so an instance an operator set up by hand
  // reads as "OpenRouter credits" no matter how long it has been on ChatGPT.
  // Asking the box is the only reliable answer, and the /provider call also
  // repairs the stored flag on its way through.
  //
  // Three reads in increasing cost, each painting as it lands: our own row
  // (instant), the box's provider (~27s, decides which panel is correct), then
  // plan usage (~26s, decoration only).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const panel = await loadPanel();
      if (!cancelled && panel.ok) setAccountEmail(panel.value.accountEmail);

      const live = await loadProvider();
      if (cancelled || !live.ok) return;
      setProvider(live.value);
      if (live.value.accountEmail !== null) setAccountEmail(live.value.accountEmail);
      // Our stored flag disagreed with the instance. The read above has already
      // corrected it server-side; tell the dashboard so the AI cell catches up
      // without a reload. An instance awaiting activation is on an `openai/`
      // model by design: that is not a disagreement, and it has no plan usage
      // worth a ~26s read.
      const liveOnChatGpt = live.value.runningOn === "ChatGpt" && !awaitingActivation;
      if (liveOnChatGpt !== linked) {
        onChangedRef.current(live.value.accountEmail);
      }

      if (!liveOnChatGpt) return;
      const u = await loadUsage();
      if (!cancelled && u.ok) setUsage(u.value.usage);
    })();
    return () => {
      cancelled = true;
    };
  }, [linked, awaitingActivation, loadPanel, loadProvider, loadUsage]);

  // Tick the code's countdown. Stops at zero rather than going negative so the
  // "expired" copy is a stable state, not a growing negative number.
  useEffect(() => {
    if (code === null) return;
    const id = window.setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [code]);

  // The code step's engine. This poll does two jobs: it collects the device code
  // once the instance has rendered it (~40s — startLink deliberately doesn't
  // wait), and it watches for the OAuth profile landing so the flow can advance
  // on its own if the customer finishes before coming back to the tab.
  //
  // Because the code lives in a file on the box rather than in an in-flight
  // request, none of this is lost to a dropped connection, a closed laptop or a
  // ztc-core redeploy: reopening the modal picks the same login back up.
  useEffect(() => {
    if (step !== "code") return;
    const id = window.setInterval(() => {
      if (statusInFlight.current) return;
      statusInFlight.current = true;
      void (async () => {
        try {
          const res = await refreshStatus();
          if (res.ok) setStatus(res.value);
        } finally {
          statusInFlight.current = false;
        }
      })();
    }, code === null ? CODE_POLL_MS : AUTH_POLL_MS);
    return () => window.clearInterval(id);
  }, [step, code, refreshStatus]);

  // Adopt whatever code the instance is currently showing. Keyed on the code
  // itself so the 15-minute countdown starts when it actually appears, not when
  // the request that launched the login returned.
  useEffect(() => {
    const url = status?.verificationUrl ?? null;
    const userCode = status?.userCode ?? null;
    if (url === null || userCode === null) return;
    if (code !== null && code.userCode === userCode) return;
    setCode({ url, userCode });
    setRemaining(CODE_TTL_SECONDS);
  }, [status, code]);

  // Count how long we've been waiting for a code so a login that dies on launch
  // surfaces a retry rather than an indefinite spinner.
  useEffect(() => {
    if (step !== "code" || code !== null) return;
    const id = window.setInterval(() => setWaited((w) => w + 1), 1000);
    return () => window.clearInterval(id);
  }, [step, code]);

  // Poll the preflight while the Codex add-on installs (~4.5 min cold). This
  // runs THROUGH the setting step on purpose: flipping the ChatGPT toggle is
  // time the customer was going to spend anyway, so the install hides behind it.
  useEffect(() => {
    if (prepareState !== "Installing") return;
    const id = window.setInterval(() => {
      if (prepareInFlight.current) return;
      prepareInFlight.current = true;
      void (async () => {
        try {
          const res = await pollPrepare();
          if (!res.ok) return;
          setPrepareState(res.value.state);
          if (res.value.state === "Failed") {
            setError(res.value.reason ?? "Your instance isn't ready for this yet.");
          }
        } finally {
          prepareInFlight.current = false;
        }
      })();
    }, 5000);
    return () => window.clearInterval(id);
  }, [prepareState, pollPrepare]);

  // Launch a login and move to the code step immediately. The code arrives on
  // the status poll, so this returns in a couple of seconds. Clearing both
  // pieces of state first matters: the box has just killed the previous login
  // and deleted its transcript, so any code still on screen is dead.
  const requestCode = useCallback(async (): Promise<void> => {
    setError(null);
    setCode(null);
    setStatus(null);
    setWaited(0);
    const res = await startLink();
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setStep("code");
  }, [startLink]);

  const handleStart = async (): Promise<void> => {
    setError(null);
    setPreparing(true);
    // Preflight first: the version floor and the Codex add-on are the two things
    // that can make this impossible, and finding out later would waste the
    // customer's time on a flow that was never going to complete.
    const prep = await prepare();
    setPreparing(false);
    if (!prep.ok) {
      setError(prep.message);
      return;
    }
    setPrepareState(prep.value.state);
    if (prep.value.state === "Failed") {
      setError(prep.value.reason ?? "Your instance isn't ready for this yet.");
      return;
    }
    // Straight to the setting, NOT the code: the code's 15-minute clock must
    // not start while they are still hunting for a toggle in ChatGPT. Move on
    // even while the add-on is still installing — the poll above gates the
    // button at the end of that step, so the install runs behind their reading.
    setStep("setting");
  };

  // An ordering change answers only the account question, so it is merged into
  // the provider state rather than replacing it: the model the agent runs on is
  // untouched, and the endpoint does not re-read it.
  const applyAccounts = (accounts: ChatGptAccountsInfo): void => {
    setProvider((p) =>
      p === null
        ? p
        : { ...p, accountEmail: accounts.accountEmail, linkedAccounts: accounts.linkedAccounts },
    );
    setAccountEmail(accounts.accountEmail);
  };

  const handleConfirm = async (): Promise<void> => {
    setError(null);
    setApplying(false);
    const linkedNow = status?.accountEmail ?? null;
    // A spare is purely an ordering change: the account is already connected by
    // this point, and the agent keeps billing whoever it was billing.
    if (relinkIntent === "fallback") {
      if (linkedNow === null) {
        setError("We can't see the new account yet. Give it a moment, then try again.");
        setCooldown(RETRY_COOLDOWN_SECONDS);
        return;
      }
      const added = await addFallbackAccount(linkedNow);
      if (added.ok) {
        applyAccounts(added.value);
        setBackupAdded({ backup: linkedNow, billed: added.value.accountEmail });
        setStep("done");
        // A spare moves nobody's billing — the account that was paying still is.
        onChanged(added.value.accountEmail);
        return;
      }
      setError(added.message);
      setCooldown(RETRY_COOLDOWN_SECONDS);
      return;
    }
    // Remembered before the request, because the settle poll below has to be
    // able to tell "the box is on ChatGPT" from "the box is on the account we
    // just linked" — on a re-link only the second one means anything.
    pendingAccount.current = linkedNow;
    const res = await activate();
    if (res.ok) {
      setStatus(res.value);
      setStep("done");
      // The account we just cut over to is the one that pays from here.
      onChanged(res.value.accountEmail ?? linkedNow);
      return;
    }
    // A coded answer from ztc-core is a real one ("that account isn't connected
    // yet") and belongs on screen as written. UNKNOWN is the edge giving up on a
    // slow origin - and since applying the cutover restarts the instance, being
    // briefly unable to reach it is the expected path, not a failure. Say that,
    // hold the button, and let the settle-poll finish the job.
    if (res.code === "UNKNOWN") {
      setApplying(true);
      setCooldown(RETRY_COOLDOWN_SECONDS);
      return;
    }
    setError(res.message);
    setCooldown(RETRY_COOLDOWN_SECONDS);
  };

  // Connect a DIFFERENT ChatGPT account over the top of the current one. The
  // backend already handles this: startLogin records which accounts were on the
  // box first, so the one that appears afterwards is reported as the new link.
  // Skips the intro — they are already on a subscription and have seen it.
  const handleRelink = async (intent: "default" | "fallback" = "default"): Promise<void> => {
    setError(null);
    setBackupAdded(null);
    setRelinkIntent(intent);
    setPreparing(true);
    const prep = await prepare();
    setPreparing(false);
    if (!prep.ok) {
      setError(prep.message);
      return;
    }
    setPrepareState(prep.value.state);
    if (prep.value.state === "Failed") {
      setError(prep.value.reason ?? "Your instance isn't ready for this yet.");
      return;
    }
    setRelinking(true);
    setStep("setting");
  };

  const copyCode = async (): Promise<void> => {
    if (code === null) return;
    try {
      await navigator.clipboard.writeText(code.userCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is permission-gated in some browsers; the code is on screen
      // in large type either way, so this is not worth an error state.
    }
  };

  const header = (title: string): JSX.Element => (
    <header className={dialogStyles.dialogHeader}>
      <h3 className={dialogStyles.dialogTitle} id={titleId}>
        {title}
      </h3>
      <button
        type="button"
        className={dialogStyles.closeBtn}
        onClick={onClose}
        aria-label="Close"
        disabled={busy}
      >
        ×
      </button>
    </header>
  );

  const installing = prepareState === "Installing";
  // The install runs in the background THROUGH the setting step, so it can fail
  // while the customer is still reading. Gate the "I've turned it on" button on
  // Ready rather than on !installing: a Failed state clears `installing`, and
  // re-enabling there walks them into a device login on an instance with no
  // Codex plugin — a login that can never produce a code they can use.
  const prepareFailed = prepareState === "Failed";
  const canRequestCode = prepareState === "Ready";
  // The instance's answer wins as soon as we have it; `linked` (our database) is
  // only a seed, and is wrong for any instance set up outside this flow. Not for
  // an instance awaiting activation, whose box reads as ChatGPT from the start.
  // The account is deliberately not part of this test: an unreadable auth store
  // reports no account on a tenant that is genuinely connected.
  const onChatGpt = provider !== null ? provider.runningOn === "ChatGpt" && !awaitingActivation : linked;

  // Promote an already-connected account to the one that gets billed. No login,
  // no model change - just a reordering, so it is fast and reversible.
  const handleMakeDefault = async (email: string): Promise<void> => {
    setError(null);
    const res = await setDefaultAccount(email);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    applyAccounts(res.value);
    onChanged(res.value.accountEmail);
  };

  // While a cutover settles, ask the instance what it is ACTUALLY running on.
  // The switch has usually landed and only the edge gave up waiting, so this is
  // what turns a red box into a finished flow with no further clicks.
  //
  // "Landed" is deliberately not just `runningOn === "ChatGpt"`. The relink flow
  // is only reachable from the connected panel, so the box is ALREADY on an
  // `openai/` primary and that test passes on the first tick five seconds in —
  // before any cutover could have happened, and equally when the activate never
  // reached ztc-core at all. The account being billed is what the switch moves,
  // so that is what confirms it.
  useEffect(() => {
    if (!applying) return undefined;
    const startedAt = Date.now();
    const target = pendingAccount.current;
    let cancelled = false;
    const id = setInterval(() => {
      // /provider is ~27s and the interval is 5s, so without this every tick
      // would stack another SSM round-trip onto an instance that is already
      // restarting — the same feedback loop the confirm-step cooldown removes.
      if (providerInFlight.current) return;
      // A cutover that failed on the box never lands, and polling for it forever
      // both lies to the customer and keeps spending ~27s reads on the instance.
      if (Date.now() - startedAt > APPLYING_TIMEOUT_MS) {
        setApplying(false);
        setError(
          "Your instance hasn't come back with the switch applied. Open it to check it's " +
            "healthy, then try again — or get in touch and we'll take a look.",
        );
        return;
      }
      providerInFlight.current = true;
      void (async () => {
        try {
          // An instance awaiting activation already reads as ChatGPT with the
          // new account's profile, so the box cannot confirm the cutover. The
          // recorded link can: activation writes it only after the config has
          // been applied and verified, and reading it is instant.
          if (awaitingActivation) {
            const panel = await loadPanel();
            if (cancelled || !panel.ok) return;
            const recorded = panel.value.accountEmail;
            if (recorded !== null && (target === null || recorded === target)) {
              setApplying(false);
              setStep("done");
              onChanged(recorded);
            }
            return;
          }
          const res = await loadProvider();
          if (cancelled || !res.ok) return;
          setProvider(res.value);
          const landed =
            res.value.runningOn === "ChatGpt" &&
            (target === null || res.value.accountEmail === target);
          if (landed) {
            setApplying(false);
            setStep("done");
            onChanged(res.value.accountEmail);
          }
        } finally {
          providerInFlight.current = false;
        }
      })();
    }, APPLYING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [applying, awaitingActivation, loadPanel, loadProvider, onChanged]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Not an error: the instance is restarting to apply what we just sent it.
  const applyingBanner = !applying ? null : (
    <p className={styles.troubleshoot} data-testid="chatgpt-applying">
      Applying the switch — your instance is restarting, which takes about a
      minute. This page will finish on its own.
    </p>
  );

  const errorBanner =
    error === null ? null : (
      <p className={dialogStyles.errorBanner} data-testid="chatgpt-error">
        {error}
      </p>
    );

  // --- already on a ChatGPT subscription -----------------------------------
  // Facts first, as a scannable table; prose only for the two things a table
  // can't say. The previous version was four lines of wrapped mono, which is
  // where the important number (what's left of the plan) went to hide.
  //
  // Gated on what the INSTANCE reports, falling back to our stored flag only
  // until the box has answered. An instance wired up by hand has a null flag but
  // is genuinely on ChatGPT, and it should see this panel, not an offer to
  // switch to something it is already using.
  //
  // Never over a finished switch: the success screen carries the restart advice,
  // and a ChatGPT-mode instance's recorded link lands on the next dashboard
  // snapshot, seconds after activation, which would otherwise replace it.
  if (onChatGpt && !relinking && step !== "done") {
    const percentLeft = usage?.percentLeft ?? null;
    return (
      <ModalShell onClose={busy ? () => undefined : onClose} titleId={titleId} size="md">
        {header("Running on your ChatGPT subscription")}
        <div data-testid="chatgpt-linked-panel">
          <dl className={styles.factGrid}>
            <div className={styles.fact}>
              <dt>Account</dt>
              <dd data-testid="chatgpt-linked-account">
                {accountEmail ?? <span className={styles.pending}>checking…</span>}
              </dd>
            </div>
            <div className={styles.fact}>
              <dt>Model</dt>
              <dd>{provider?.activePrimary ?? <span className={styles.pending}>checking…</span>}</dd>
            </div>
          </dl>

          {/* The plan's remaining allowance — the number that decides whether
              the agent keeps answering on ChatGPT or drops to the fallback. */}
          {percentLeft !== null ? (
            <div className={styles.usageBlock} data-testid="chatgpt-usage">
              <div className={styles.usageHead}>
                <span className={styles.usageLabel}>Plan usage left</span>
                <span className={styles.usageValue}>{percentLeft}%</span>
              </div>
              <div
                className={styles.usageTrack}
                data-state={percentLeft <= 10 ? "alert" : percentLeft <= 30 ? "warn" : "calm"}
              >
                <span className={styles.usageFill} style={{ width: `${percentLeft}%` }} />
              </div>
              <p className={styles.usageMeta}>
                {usage?.windowHours === 168 ? "This week" : `Current window`}
                {usage?.resetsLabel != null ? ` · resets ${usage.resetsLabel}` : ""}
              </p>
            </div>
          ) : null}

          {/* The instance read takes ~10s, and popping a whole accounts section
              in afterwards made the panel look like it had finished when it
              hadn't. Hold the space with a skeleton that matches the real rows,
              so "you may have other accounts" is visible while we find out. */}
          {provider === null ? (
            <div className={styles.usageBlock} data-testid="chatgpt-accounts-loading">
              <p className={styles.usageLabel}>Connected accounts</p>
              <ul className={styles.accountList}>
                <li className={styles.accountRow}>
                  <Skeleton width="11rem" height="0.8125rem" />
                </li>
                <li className={styles.accountRow}>
                  <Skeleton width="8rem" height="0.8125rem" />
                </li>
              </ul>
              <p className={styles.usageMeta}>Checking which accounts are connected…</p>
            </div>
          ) : provider.linkedAccounts.length > 1 ? (
            <div className={styles.usageBlock} data-testid="chatgpt-accounts">
              <p className={styles.usageLabel}>Connected accounts</p>
              <ul className={styles.accountList}>
                {provider.linkedAccounts.map((email, i) => (
                  <li key={email} className={styles.accountRow}>
                    <span className={styles.accountEmail}>{email}</span>
                    {i === 0 ? (
                      <span className={styles.accountBadge} data-testid="chatgpt-account-default">
                        primary
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={styles.rowBtn}
                        onClick={() => void handleMakeDefault(email)}
                        disabled={busy || preparing}
                        data-testid={`chatgpt-make-default-${i}`}
                      >
                        Make primary
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <p className={styles.usageMeta}>
                Your agent uses the primary account, and only falls back to the backups
                when it can't.
              </p>
            </div>
          ) : null}

          <p className={styles.note}>
            Your agent uses your connected ChatGPT accounts. If none can serve a request,
            it stops until usage resets or you connect an available account. It does not
            automatically switch to OpenRouter.
          </p>

          {errorBanner}
          <footer className={dialogStyles.footer}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => void handleRelink("default")}
              disabled={busy || preparing}
              data-testid="chatgpt-relink"
            >
              {preparing ? "Getting your instance ready…" : "Switch to another account"}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => void handleRelink("fallback")}
              disabled={busy || preparing}
              data-testid="chatgpt-add-fallback"
            >
              Add a backup account
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onClose}
              disabled={busy || preparing}
            >
              Done
            </button>
          </footer>
        </div>
      </ModalShell>
    );
  }

  // --- step 4: done, having added a spare ----------------------------------
  // Its own screen, not the switch one. A customer who deliberately chose "add a
  // backup" over "switch" would otherwise be told the backup is what runs their
  // instance now — and none of that is true: the order gained a tail entry, no
  // model moved, and the same account keeps paying.
  if (step === "done" && backupAdded !== null) {
    return (
      <ModalShell onClose={onClose} titleId={titleId} size="sm">
        {header("Backup account connected")}
        <div data-testid="chatgpt-backup-success">
          <p className={styles.successTitle}>
            <strong>{backupAdded.backup}</strong> is connected as a backup.
          </p>
          <p className={styles.restartHint}>
            {backupAdded.billed === null
              ? "Nothing about what your agent runs on has changed."
              : `${backupAdded.billed} still pays for your agent's work.`}{" "}
            <strong>{instanceName}</strong> only falls back to the backup when the account
            in front of it can't serve — usually because its weekly ChatGPT allowance has
            run out.
          </p>
          <footer className={dialogStyles.footer}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onClose}
              data-testid="chatgpt-backup-done"
              autoFocus
            >
              Done
            </button>
          </footer>
        </div>
      </ModalShell>
    );
  }

  // --- step 4: done --------------------------------------------------------
  if (step === "done") {
    return (
      <ModalShell onClose={onClose} titleId={titleId} size="sm">
        {header("You're on ChatGPT")}
        <div data-testid="chatgpt-success">
          <p className={styles.successTitle}>
            <strong>{instanceName}</strong> is now running on{" "}
            {status?.accountEmail ?? "your ChatGPT subscription"}.
          </p>
          <p className={styles.restartHint}>
            Your instance is restarting to load the change. Give it about 15 seconds
            before you open it — opening it right away may show a "couldn't reach your
            instance" message.
          </p>
          <footer className={dialogStyles.footer}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onClose}
              data-testid="chatgpt-done"
              autoFocus
            >
              Done
            </button>
          </footer>
        </div>
      </ModalShell>
    );
  }

  // --- step 2: the ChatGPT setting ----------------------------------------
  // Before the code, never after. ChatGPT refuses the code at the moment it is
  // entered unless this toggle is already on.
  if (step === "setting") {
    return (
      <ModalShell
        onClose={busy || preparing ? () => undefined : onClose}
        titleId={titleId}
        size="md"
      >
        {header("Turn on device code sign-in")}
        <div data-testid="chatgpt-setting-step">
          <p className={styles.explainer}>
            ChatGPT won't accept a code from your agent until you switch this on. It's
            off by default, and it's the one thing you have to change in your own
            ChatGPT account.
          </p>

          <ol className={styles.stepList}>
            <li>
              Open{" "}
              <a href={CHATGPT_SETTINGS_URL} target="_blank" rel="noreferrer">
                your ChatGPT settings
              </a>
              .
            </li>
            <li>
              Go to <strong>Security and login</strong>.
            </li>
            <li>
              Scroll to the bottom and turn on{" "}
              <strong>{CHATGPT_DEVICE_CODE_SETTING}</strong>.
            </li>
          </ol>

          <p className={styles.note}>
            We'll give you your code on the next screen. It only lasts 15 minutes, so
            do this first.
          </p>

          {/* The Codex add-on installs on the instance while they're doing the
              above. It's usually done before they get here; when it isn't, the
              button waits rather than handing out a code the box can't use. */}
          {installing ? (
            <p className={styles.statusLine} data-testid="chatgpt-installing">
              Still getting your instance ready in the background. This takes a few
              minutes the first time, and you can carry on with the steps above.
            </p>
          ) : null}

          {errorBanner}

          <footer className={dialogStyles.footer}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setStep("intro")}
              disabled={busy}
            >
              Back
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => void requestCode()}
              disabled={busy || !canRequestCode}
              data-testid="chatgpt-setting-done"
            >
              {installing
                ? "Getting your instance ready…"
                : prepareFailed
                  ? "Can't continue"
                  : busy
                    ? "Getting your code…"
                    : "I've turned it on"}
            </button>
          </footer>
        </div>
      </ModalShell>
    );
  }

  // --- step 3: the code ----------------------------------------------------
  // Rendered before the code exists: it takes the instance ~40s to produce one,
  // and it arrives on the status poll rather than with the request that launched
  // the login. Waiting here with a spinner beats holding an HTTP request open,
  // which is what used to 504 at the edge.
  if (step === "code") {
    const expired = remaining === 0;
    const authorized = status?.state === "Authorized";
    const slow = code === null && waited > CODE_WAIT_WARN_SECONDS;
    return (
      <ModalShell onClose={busy ? () => undefined : onClose} titleId={titleId} size="md">
        {header("Enter your code at ChatGPT")}
        <div data-testid="chatgpt-code-step">
          <p className={styles.explainer}>
            Open the link below, sign in with the ChatGPT account your subscription is
            on, and enter this code.
          </p>

          <div className={styles.codeBlock}>
            <span className={styles.code} data-testid="chatgpt-code">
              {code?.userCode ?? "· · · · · · ·"}
            </span>
            {code !== null ? (
              <button
                type="button"
                className={styles.copyBtn}
                onClick={() => void copyCode()}
                data-testid="chatgpt-copy"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            ) : null}
          </div>

          {code !== null ? (
            <a
              className={styles.openLink}
              href={code.url}
              target="_blank"
              rel="noreferrer"
              data-testid="chatgpt-open-link"
            >
              Open {hostOf(code.url)} →
            </a>
          ) : null}

          <p className={styles.statusLine} data-testid="chatgpt-countdown">
            {code === null
              ? slow
                ? "Your instance is taking longer than usual to produce a code. Try asking for a new one."
                : "Getting your code from your instance. This takes about a minute."
              : authorized
                ? "Connected — we can see your ChatGPT account."
                : expired
                  ? "That code has expired — get a fresh one to carry on."
                  : `This code works for another ${formatCountdown(remaining)}.`}
          </p>

          {/* The one error customers actually hit. ChatGPT shows it at the
              moment the code is entered, and it is unrecoverable for THAT code
              — so name it, and make the way back obvious. */}
          <p className={styles.troubleshoot}>
            Seeing <em>"{CHATGPT_DEVICE_CODE_SETTING}"</em> as an error?{" "}
            <button
              type="button"
              className={styles.inlineLink}
              onClick={() => setStep("setting")}
              data-testid="chatgpt-back-to-setting"
            >
              That setting isn't on yet
            </button>{" "}
            — switch it on, then come back for a fresh code.
          </p>

          {applyingBanner}
          {applying ? null : errorBanner}

          <footer className={dialogStyles.footer}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => void requestCode()}
              disabled={busy || applying}
              data-testid="chatgpt-new-code"
            >
              {busy ? "Getting a new code…" : "Get a new code"}
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => void handleConfirm()}
              // Held for the WHOLE settle, not just the cooldown. The cooldown
              // lapses after 15s while the instance is still restarting, and a
              // button that goes live there invites the retry that costs the box
              // another cutover's worth of work.
              disabled={busy || applying || code === null || cooldown > 0}
              data-testid="chatgpt-confirm"
            >
              {busy
                ? "Switching your agent over…"
                : applying
                  ? "Checking your instance…"
                  : cooldown > 0
                    ? `Try again in ${cooldown}s`
                    : "I've entered the code"}
            </button>
          </footer>
        </div>
      </ModalShell>
    );
  }

  // --- step 1: what changes ------------------------------------------------
  return (
    <ModalShell onClose={busy || preparing ? () => undefined : onClose} titleId={titleId} size="md">
      {header("Connect your ChatGPT subscription")}
      <div data-testid="chatgpt-intro-step">
        <p className={styles.explainer}>
          Connect <strong>{instanceName}</strong> to your own ChatGPT subscription.
        </p>

        <ul className={styles.pointList}>
          <li>Your agent's work uses the allowance of the ChatGPT account you connect.</li>
          <li>Subscription usage limits apply. If the account is unavailable, your agent cannot
            respond until you reconnect, add an available backup account, or usage resets.</li>
          <li>There is no automatic OpenRouter fallback and no OpenRouter key is required.</li>
        </ul>

        <p className={styles.note}>
          You'll need an active ChatGPT subscription and a browser signed in to that
          account. There's one setting to flip in ChatGPT first — we'll show you exactly
          where. The whole thing takes a couple of minutes. Not sure? Email us at{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>

        <label className={styles.ackRow}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="chatgpt-ack"
          />
          <span>
            I understand my agent will use my ChatGPT subscription's usage allowance.
          </span>
        </label>

        {errorBanner}

        <footer className={dialogStyles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={busy || preparing}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => void handleStart()}
            disabled={busy || preparing || !acknowledged}
            data-testid="chatgpt-start"
          >
            {preparing
              ? "Getting your instance ready…"
              : busy
                ? "Requesting your code…"
                : "Start"}
          </button>
        </footer>
      </div>
    </ModalShell>
  );
};
